import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { baixarArquivo, request } from '@/lib/http';
import {
  type AtualizarCentroCustoRequest,
  centroCustoSchema,
  type CriarCentroCustoRequest,
  paginaCentrosCustoSchema,
  resultadoImportacaoCentrosCustoSchema,
} from '@fiscoai/contracts';

/** Quantos por página no cadastro: acima disso, a busca refina (nunca carrega tudo). */
export const CENTROS_POR_PAGINA = 100;

const chave = (empresaId: string) => ['centros-custo', empresaId];

export function useCentrosCusto(empresaId: string, busca: string) {
  return useQuery({
    queryKey: [...chave(empresaId), busca],
    queryFn: ({ signal }) =>
      request(`/empresas/${empresaId}/centros-custo`, {
        schema: paginaCentrosCustoSchema,
        query: { q: busca || undefined, limit: CENTROS_POR_PAGINA },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** Escritas recarregam a lista e as notas abertas (o grupo contábil pode mudar). */
function useEscrita<TVars, TRes>(empresaId: string, executar: (vars: TVars) => Promise<TRes>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chave(empresaId) });
      void cliente.invalidateQueries({ queryKey: ['documentos'], refetchType: 'none' });
    },
  });
}

export function useCriarCentroCusto(empresaId: string) {
  return useEscrita(empresaId, (dados: CriarCentroCustoRequest) =>
    request(`/empresas/${empresaId}/centros-custo`, {
      method: 'POST',
      body: dados,
      schema: centroCustoSchema,
    }),
  );
}

export function useAtualizarCentroCusto(empresaId: string) {
  return useEscrita(empresaId, ({ id, ...dados }: AtualizarCentroCustoRequest & { id: string }) =>
    request(`/empresas/${empresaId}/centros-custo/${id}`, {
      method: 'PATCH',
      body: dados,
      schema: centroCustoSchema,
    }),
  );
}

/** Sem `confirmar` só pré-visualiza; confirmado grava tudo ou nada. */
export function useImportarCentrosCusto(empresaId: string) {
  return useEscrita(empresaId, ({ arquivo, confirmar }: { arquivo: File; confirmar: boolean }) => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo);
    return request(`/empresas/${empresaId}/centros-custo/importacao`, {
      method: 'POST',
      body: corpo,
      query: { confirmar: String(confirmar) },
      schema: resultadoImportacaoCentrosCustoSchema,
    });
  });
}

export function baixarPlanilhaCentrosCusto(empresaId: string) {
  return baixarArquivo(`/empresas/${empresaId}/centros-custo/planilha.csv`, 'centros-de-custo.csv');
}
